import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    Box,
    Stack,
    Text,
    SimpleGrid,
    Checkbox,
    Flex,
    Grid,
    Button,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Switch} from '@chakra-ui/react'
import {ShareIcon, CopyIcon, LinkIcon, CustomCloseWhite, CustomClose, PlusIcon} from '../../components/icons'
import WebDrawer from '../../components/web-drawer'
import {shareOrCopyLink} from '../../utils/share'
import {useProducts, useShopperCustomersMutation, useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useWishlistActions, WISHLIST_MAX_ITEMS} from '../../hooks/use-wishlist-actions'
import {useProductLists} from '../../hooks/use-product-lists'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript, setInitialScriptState} from '../../contexts/script-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {useBrand} from '../../contexts/brand-context'
import Seo from '../../components/seo'
import RouteGuard from '../../components/route-guard'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {resolveGDIStyles, GDI_TEXT_BLOCK_FIELD_MAP} from '../../components/optimizely/gdi/utils/gdi-styles'
import {decodeHtmlEntities} from '../../components/optimizely/gdi/utils/gdi-html-entities'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import WishlistBreadcrumb from './wishlist-breadcrumb'
import WishlistEmpty, {WISHLIST_EMPTY_PAGE_ID} from './wishlist-empty'
import WishlistNoCollections from './wishlist-no-collections'
import WishlistFolderNav from '../../components/wishlist-folder-nav'
import WishlistFolderFormModal from '../../components/wishlist-folder-form-modal'
import WishlistRemoveItemsDrawer from '../../components/wishlist-remove-items-drawer'
import WishlistMoveItemsDrawer from '../../components/wishlist-move-items-drawer'
import {GDIDynamicContentProvider} from '../../components/optimizely/gdi/utils/gdi-dynamic-content-context'

const PRODUCTS_PER_PAGE = 24
const REMOVED_ITEMS_HIDE_KEY = 'wishlist_removed_items_hide'
const REMOVED_ITEMS_HIDE_TTL_MS = 15000

export const blurEventCurrentTarget = (event) => event?.currentTarget?.blur()

/**
 * Standalone Wishlist page â€” supports My Favourites view (default) and
 * individual custom folder views via /wishlist/folder/:folderId.
 *
 * Features (MVP):
 * - Multi-folder navigation (My Favourites + custom_1 folders)
 * - Folder CRUD (create, rename, delete)
 * - Batch selection: remove or move selected items
 * - Per-item remove with 2-option modal (folder-only vs all)
 * - Undo toast after batch/individual removal
 * - 250-item capacity limit (WISHLIST_MAX_ITEMS)
 * - OOS tiles displayed as greyed-out (still removable)
 * - Discontinued / expired items auto-removed on load
 */
const WishlistPage = () => {
    const {formatMessage} = useIntl()
    const {folderId} = useParams()       // set when route is /wishlist/folder/:folderId
    const {enableScript, disableScript} = useScript()
    const {app: appConfig} = getConfig()
    const {site, buildUrl} = useMultiSite()
    const siteId = site?.id
    const {isGarage} = useBrand()
    const loginPath = buildUrl('/login')
    const navigate = useNavigation()
    const styles = useMultiStyleConfig('WishlistPage', {})

    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }

    const headingRef = useRef()
    const shareDisclosure = useDisclosure()
    // Share drawer — local visibility toggle (not persisted until Save)
    const [isSharePublic, setIsSharePublic] = useState(false)
    const folderFormDisclosure = useDisclosure()
    const appOrigin = useAppOrigin()

    // Pagination
    const [page, setPage] = useState(1)
    const [allLoadedProducts, setAllLoadedProducts] = useState({})
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    // Batch selection
    const [selectedItemIds, setSelectedItemIds] = useState(new Set())
    const [isSelectMode, setIsSelectMode] = useState(false)

    // Folder modal state
    const [folderToEdit, setFolderToEdit] = useState(null)   // null = create mode
    const [pendingCreateName, setPendingCreateName] = useState('')  // pre-fill name from Move drawer
    // When non-null, create-from-move flow: modal submit won't navigate; instead selects new ID
    const [onNewFolderCreated, setOnNewFolderCreated] = useState(null)

    // Optimistic folder-deletion state (set while toast is showing, cleared on Undo or toast close)
    const [pendingDeleteFolder, setPendingDeleteFolder] = useState(null) // {folder, keepItems, folderProductIds}
    const [transientHiddenProductIds, setTransientHiddenProductIds] = useState(new Set())

    // Remove items drawer
    const removeItemsDrawerDisclosure = useDisclosure()

    // Move items drawer
    const moveItemsDrawerDisclosure = useDisclosure()

    useEffect(() => {
        if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId) === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }
        headingRef?.current?.focus()
        return () => {
            if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId)) {
                enableScript('noibu')
            }
        }
    }, [])

    // ---- CMS inline directive: open the create-collection modal ----
    // Mirrors the global login/register drawers in _app: a CMS link authored as
    // {link: {linkType: 'drawer', drawerId: 'new-collection'}} dispatches a
    // `gdi:openDrawer` event that we handle here to open the New Collection modal.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined
        const handleOpenDrawer = (event) => {
            if (event?.detail?.drawer === 'new-collection') {
                setFolderToEdit(null)
                folderFormDisclosure.onOpen()
            }
        }
        window.addEventListener('gdi:openDrawer', handleOpenDrawer)
        return () => window.removeEventListener('gdi:openDrawer', handleOpenDrawer)
    }, [])

    // ---- Data fetching ----
    // All product lists (My Favourites + custom folders)
    const {
        wishList: wishListData,
        customFolders,
        isLoading: isListsLoading,
        refetch: refetchAllLists
    } = useProductLists()

    // Determine active list: folder view or My Favourites
    const activeList = useMemo(() => {
        if (folderId && customFolders?.length) {
            return customFolders.find((f) => f.id === folderId) ?? wishListData
        }
        return wishListData
    }, [folderId, customFolders, wishListData])

    // Restore any short-lived hidden IDs persisted during collection deletion
    // so removed items never flash while navigating back to My Favourites.
    useEffect(() => {
        if (typeof window === 'undefined') return
        try {
            const raw = sessionStorage.getItem(REMOVED_ITEMS_HIDE_KEY)
            if (!raw) return
            const parsed = JSON.parse(raw)
            const expiresAt = Number(parsed?.expiresAt || 0)
            const ids = Array.isArray(parsed?.ids) ? parsed.ids.filter(Boolean) : []
            if (!ids.length || Date.now() > expiresAt) {
                sessionStorage.removeItem(REMOVED_ITEMS_HIDE_KEY)
                return
            }
            setTransientHiddenProductIds(new Set(ids))
        } catch (e) {
            // Ignore malformed cache entries
        }
    }, [])

    // Auto-remove expired items (> 1 year) from My Favourites on load
    const {mutateAsync: deleteListItem} = useShopperCustomersMutation('deleteCustomerProductListItem')
    const {data: customer} = useCurrentCustomer()
    const customerId = useCustomerId()
    const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000

    useEffect(() => {
        if (!wishListData?.customerProductListItems?.length || !customerId) return
        const now = Date.now()
        const toRemove = wishListData.customerProductListItems.filter((item) => {
            const addedAt = item.creationDate ? new Date(item.creationDate).getTime() : null
            return addedAt && now - addedAt > ONE_YEAR_MS
        })
        toRemove.forEach((item) => {
            deleteListItem({parameters: {customerId, listId: wishListData.id, itemId: item.id}}).catch(() => {})
        })
    }, [wishListData?.id])

    // Reset pagination when the active list changes (e.g. switching folders).
    // Keep the product cache to avoid a brief skeleton flash for products that
    // are already loaded and still present in the next view.
    useEffect(() => {
        setPage(1)
        setSelectedItemIds(new Set())
        setIsSelectMode(false)
    }, [activeList?.id])

    // When a folder deletion becomes pending, reset to page 1 so My Favourites
    // products load from the beginning (not from wherever the folder view was).
    // Preserve the cache to avoid rendering skeletons for products already in memory.
    useEffect(() => {
        if (pendingDeleteFolder) {
            setPage(1)
        }
    }, [!!pendingDeleteFolder])

    // While a folder deletion is pending, show My Favourites content (minus the
    // about-to-be-deleted items) so the user sees the final state during the Undo window.
    const displayList = pendingDeleteFolder ? wishListData : activeList
    const pendingHideIds = (pendingDeleteFolder && !pendingDeleteFolder.keepItems)
        ? new Set(pendingDeleteFolder.folderProductIds ?? [])
        : new Set()
    const hiddenProductIds = useMemo(() => {
        const merged = new Set(pendingHideIds)
        transientHiddenProductIds.forEach((id) => merged.add(id))
        return merged
    }, [pendingHideIds, transientHiddenProductIds])

    const productIds = displayList?.customerProductListItems?.map((item) => item.productId)

    const startIndex = (page - 1) * PRODUCTS_PER_PAGE
    const endIndex = page * PRODUCTS_PER_PAGE
    const currentPageProductIds = productIds?.slice(startIndex, endIndex)
    const missingCurrentPageProductIds = useMemo(
        () => (currentPageProductIds || []).filter((id) => id && !allLoadedProducts[id]),
        [currentPageProductIds, allLoadedProducts]
    )

    const totalWishlistItems = productIds?.length || 0
    useEffect(() => {
        if (totalWishlistItems === 0) {
            setPage(1)
            setAllLoadedProducts({})
        }
    }, [totalWishlistItems])

    // Clear transient hidden IDs once the latest wishlist data no longer contains
    // those products (or after fallback cleanup), preventing stale long-lived state.
    useEffect(() => {
        if (!transientHiddenProductIds.size) return
        const favouriteIds = new Set((wishListData?.customerProductListItems ?? []).map((item) => item.productId))
        const stillPresent = [...transientHiddenProductIds].filter((id) => favouriteIds.has(id))
        if (stillPresent.length > 0) return

        setTransientHiddenProductIds(new Set())
        if (typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(REMOVED_ITEMS_HIDE_KEY)
            } catch (e) {}
        }
    }, [wishListData?.customerProductListItems, transientHiddenProductIds])

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: missingCurrentPageProductIds.join(','),
                allImages: true,
                perPricebook: true
            }
        },
        {enabled: missingCurrentPageProductIds.length > 0}
    )

    useEffect(() => {
        if (productsData?.data) {
            setAllLoadedProducts((prev) => {
                const newProducts = {...prev}
                productsData.data.forEach((product) => {
                    newProducts[product.id] = product
                })
                return newProducts
            })
        }
    }, [productsData])

    const displayedProductIds = productIds?.slice(0, endIndex)

    // Use displayList (My Favourites when delete pending, otherwise activeList)
    // and exclude items that are about to be deleted.
    const wishListItems = displayList?.customerProductListItems
        ?.filter((item) => displayedProductIds?.includes(item.productId) && !hiddenProductIds.has(item.productId))
        ?.reduce((acc, item) => {
            const productData = allLoadedProducts[item.productId]
            return {...acc, [item.productId]: {...item, product: productData}}
        }, {})

    const {
        toggleWishlistPublicStatus,
        removeItemFromList,
        removeItemsFromList,
        moveItemsToList,
        revertMoveItemsToList,
        createFolder,
        renameFolder,
        deleteFolder,
        deleteFolderWithOptions
    } = useWishlistActions()
    const {showCustomToast, removeToast} = useCustomToast()

    // Undo delete refs — used for the deferred folder-deletion optimistic pattern
    const undoDeleteRef = useRef(false)

    // Undo remove refs — prevent item deletion if user clicks Undo before toast expires
    const undoRemoveRef = useRef(false)

    // ---- Share handlers ----
    const handleShareClick = () => {
        setIsSharePublic(activeList?.public ?? false)
        shareDisclosure.onOpen()
    }

    const handleShareDrawerSave = async () => {
        if (isSharePublic !== (activeList?.public ?? false)) {
            await toggleWishlistPublicStatus(isSharePublic, activeList?.id)
        }
        shareDisclosure.onClose()
    }

    const handleShareDrawerCancel = () => {
        setIsSharePublic(activeList?.public ?? false)
        shareDisclosure.onClose()
    }

    const ensurePublic = async () => {
        if (isSharePublic && !(activeList?.public)) {
            await toggleWishlistPublicStatus(isSharePublic, activeList?.id)
        }
    }

    const handleCopyLink = async () => {
        await ensurePublic()
        const wishlistUrl = `${appOrigin}/share-wishlist/${activeList?.id}`
        await navigator.clipboard.writeText(wishlistUrl)
        showCustomToast({message: 'Link copied'})
    }

    const handleShareAction = () => {
        const wishlistUrl = `${appOrigin}/share-wishlist/${activeList?.id}`
        shareOrCopyLink({
            url: wishlistUrl,
            title: 'My Wishlist',
            text: 'Check out my wishlist',
            onCopied: () => showCustomToast({message: 'Link copied'})
        })
    }

    // ---- Unavailable product handler ----
    const handleUnavailableProducts = async (unavailableProductIds) => {
        if (!unavailableProductIds.length) return
        await Promise.all(
            unavailableProductIds.map(async (id) => {
                const item = activeList?.customerProductListItems?.find(
                    (i) => i.productId.toString() === id.toString()
                )
                if (!item) return
                await deleteListItem({
                    parameters: {customerId: customer?.customerId, itemId: item.id, listId: activeList.id}
                })
            })
        )
    }

    // ---- Batch selection helpers ----
    const allItemKeys = Object.keys(wishListItems ?? {})
    const allSelected = allItemKeys.length > 0 && selectedItemIds.size === allItemKeys.length
    const selectedCount = selectedItemIds.size

    const handleToggleSelect = useCallback((productId) => {
        setSelectedItemIds((prev) => {
            const next = new Set(prev)
            if (next.has(productId)) next.delete(productId)
            else next.add(productId)
            return next
        })
    }, [])

    const handleSelectAll = () => setSelectedItemIds(new Set(allItemKeys))
    const handleDeselectAll = () => {
        setSelectedItemIds(new Set())
        setIsSelectMode(false)
    }

    // ---- Batch remove ----
    const handleBatchRemove = async () => {
        const items = [...selectedItemIds].map((productId) => {
            const entry = wishListItems[productId]
            return {itemId: entry.id, productId}
        })
        setSelectedItemIds(new Set())
        setIsSelectMode(false)
        await removeItemsFromList({listId: activeList?.id, items})
    }

    // ---- Remove items drawer ----
    const handleOpenRemoveItemsDrawer = () => {
        removeItemsDrawerDisclosure.onOpen()
    }

    const handleRemoveItemsConfirm = ({mode}) => {
        // Capture all reactive data before clearing state
        const items = [...selectedItemIds].map((productId) => {
            const entry = wishListItems[productId]
            return {itemId: entry.id, productId}
        })
        const activeFolderListId = activeList?.id
        const rootListId = wishListData?.id
        const wasViewingFolder = isViewingFolder
        // Pre-compute root item IDs for "remove from everywhere" in folder view
        const rootItems =
            mode !== 'folder' && wasViewingFolder
                ? items
                      .map((i) => {
                          const fi = wishListData?.customerProductListItems?.find(
                              (fi) => fi.productId === i.productId
                          )
                          return fi ? {itemId: fi.id, productId: i.productId} : null
                      })
                      .filter(Boolean)
                : []

        // Pre-compute items in OTHER custom folders (for "remove from all collections" mode).
        // When wasViewingFolder=true, activeFolderListId is already handled by the main path.
        // When wasViewingFolder=false, the user is on My Favourites — also clean all folders.
        const otherFolderItemsByList =
            mode !== 'folder'
                ? (customFolders ?? [])
                      .filter((f) => f.id !== activeFolderListId)
                      .reduce((acc, folder) => {
                          const toRemove = items
                              .map((item) => {
                                  const fi = folder.customerProductListItems?.find(
                                      (fi) => fi.productId === item.productId
                                  )
                                  return fi ? {itemId: fi.id, productId: item.productId} : null
                              })
                              .filter(Boolean)
                          if (toRemove.length) acc[folder.id] = toRemove
                          return acc
                      }, {})
                : {}

        setSelectedItemIds(new Set())
        setIsSelectMode(false)
        removeItemsDrawerDisclosure.onClose()

        undoRemoveRef.current = false

        const count = items.length
        const toastMessage =
            count === 1
                ? formatMessage(
                      {id: 'wishlist.info.item_removed_count', defaultMessage: '{count} item has been removed.'},
                      {count}
                  )
                : formatMessage(
                      {id: 'wishlist.info.items_removed_count', defaultMessage: '{count} items have been removed.'},
                      {count}
                  )

        const toastId = showCustomToast({
            message: toastMessage,
            actionLabel: formatMessage({id: 'wishlist.action.undo', defaultMessage: 'Undo'}),
            onActionClick: () => {
                undoRemoveRef.current = true
                removeToast(toastId)
            },
            onClose: async () => {
                if (undoRemoveRef.current) return
                if (mode === 'folder' && wasViewingFolder) {
                    await removeItemsFromList({listId: activeFolderListId, items, skipToast: true})
                } else if (wasViewingFolder) {
                    if (items.length > 0)
                        await removeItemsFromList({listId: activeFolderListId, items, skipToast: true})
                    if (rootItems.length > 0)
                        await removeItemsFromList({listId: rootListId, items: rootItems, skipToast: true})
                } else {
                    await removeItemsFromList({listId: rootListId, items, skipToast: true})
                }
                // Remove from all other folders that contain these products
                for (const [listId, folderItems] of Object.entries(otherFolderItemsByList)) {
                    await removeItemsFromList({listId, items: folderItems, skipToast: true})
                }
            }
        })
    }

    // ---- Batch move ----
    const handleBatchMoveApply = async ({targetIds}) => {
        const items = [...selectedItemIds].map((productId) => ({
            itemId: wishListItems[productId]?.id,
            productId
        }))
        const fromListId = activeList?.id
        const rootListId = wishListData?.id

        setSelectedItemIds(new Set())
        setIsSelectMode(false)

        const count = items.length
        const toastMessage =
            count === 1
                ? formatMessage(
                      {id: 'wishlist.info.item_moved_count', defaultMessage: '1 Item has been moved.'}
                  )
                : formatMessage(
                      {id: 'wishlist.info.items_moved_count', defaultMessage: '{count} Items have been moved.'},
                      {count}
                  )

        // Perform the move immediately so the source and destination collections update right
        // away (moveItemsToList refetches the lists on completion). The toast is only a
        // confirmation and must not gate or delay the actual move.
        const moveResult = await moveItemsToList({
            fromListId,
            wishListId: rootListId,
            toListIds: targetIds,
            items,
            skipToast: true
        })

        // Confirmation toast. The move has already happened; clicking Undo (while the toast is
        // still visible) reverts it by removing the items from the destination(s) and restoring
        // them to the source folder when applicable.
        const toastId = showCustomToast({
            message: toastMessage,
            actionLabel: formatMessage({id: 'wishlist.action.undo', defaultMessage: 'Undo'}),
            onActionClick: async () => {
                removeToast(toastId)
                await revertMoveItemsToList({
                    fromListId,
                    wishListId: rootListId,
                    toListIds: targetIds,
                    items,
                    addedByListId: moveResult?.addedByListId
                })
            }
        })
    }

    const handleCreateCollectionInDrawer = async (name) => {
        return await createFolder(name)
    }

    // ---- Per-item remove — reuses the same Remove Items drawer ----
    const handleRequestRemoveItem = (item) => {
        setSelectedItemIds(new Set([item.productId]))
        removeItemsDrawerDisclosure.onOpen()
    }

    // ---- Folder management ----
    const handleCreateFolder = (event) => {
        // iOS Safari can retain the tapped button's focus while the drawer is open,
        // causing its black interaction state to reappear when the drawer closes.
        blurEventCurrentTarget(event)
        setFolderToEdit(null)
        folderFormDisclosure.onOpen()
    }

    const handleRenameFolder = (folder) => {
        setFolderToEdit(folder)
        folderFormDisclosure.onOpen()
    }

    const handleDeleteFolder = async (folder) => {
        await deleteFolder(folder.id)
        // Navigate back to My Favourites if we just deleted the current folder
        if (folderId === folder.id) {
            navigate(buildUrl('/wishlist'))
        }
    }

    /**
     * Called when user confirms deletion inside the edit-collection drawer.
     * Uses an optimistic pattern: the folder is immediately hidden from the UI
     * (by setting pendingDeleteFolder state) without navigating away, so the
     * component stays mounted and the filter keeps working during the toast window.
     * Navigation + actual API delete happen only when the toast closes without Undo.
     */
    const handleDeleteConfirm = ({keepItems}) => {
        const folderSnapshot = folderToEdit
        const folderProductIds = (activeList?.customerProductListItems ?? []).map((i) => i.productId)
        const pending = {folder: folderSnapshot, keepItems, folderProductIds}
        const currentFolderId = folderId // capture before any navigation

        // Optimistically hide folder from nav immediately
        undoDeleteRef.current = false
        setPendingDeleteFolder(pending)

        // Close drawer — but do NOT navigate yet (navigation resets state)
        folderFormDisclosure.onClose()

        // Empty collections have no items to restore — Undo is not meaningful.
        const isEmptyFolder = folderProductIds.length === 0

        const toastId = showCustomToast({
            message: formatMessage({
                id: 'wishlist.collection.removed_toast',
                defaultMessage: '1 collection has been removed.'
            }),
            ...(!isEmptyFolder && {
                actionLabel: formatMessage({id: 'wishlist.action.undo', defaultMessage: 'Undo'}),
                onActionClick: () => {
                    undoDeleteRef.current = true
                    setPendingDeleteFolder(null) // Restore folder in nav
                    removeToast(toastId)
                    // No navigation needed — user is already on the folder page
                }
            }),
            onClose: async () => {
                if (!undoDeleteRef.current) {
                    const shouldNavigateToWishlist = currentFolderId === pending.folder?.id
                    const removedIds = !pending.keepItems
                        ? (pending.folderProductIds || []).filter(Boolean)
                        : []

                    // Keep just-removed IDs hidden while async list updates settle,
                    // so clearing pending state does not cause a brief blink.
                    if (removedIds.length) {
                        setTransientHiddenProductIds((prev) => {
                            const merged = new Set(prev)
                            removedIds.forEach((id) => merged.add(id))
                            return merged
                        })
                    }

                    // Delete before navigating to ensure the deletion API completes
                    // before the new page renders. This prevents stale data from briefly
                    // appearing on the My Favourites page.
                    await deleteFolderWithOptions({
                        folderId: pending.folder.id,
                        keepItems: pending.keepItems,
                        folderProductIds: pending.folderProductIds,
                        // Navigation will remount the page and pull fresh data; avoid
                        // a redundant pre-navigation refetch that causes an extra flicker.
                        skipRefetch: shouldNavigateToWishlist
                    })
                    if (shouldNavigateToWishlist && removedIds.length) {
                        try {
                            sessionStorage.setItem(
                                REMOVED_ITEMS_HIDE_KEY,
                                JSON.stringify({
                                    ids: removedIds,
                                    expiresAt: Date.now() + REMOVED_ITEMS_HIDE_TTL_MS
                                })
                            )
                        } catch (e) {
                            // sessionStorage unavailable — no-op fallback
                        }
                    }
                    // After deletion succeeds, navigate to My Favourites
                    if (shouldNavigateToWishlist) {
                        navigate(buildUrl('/wishlist'))
                        return
                    }
                }
                setPendingDeleteFolder(null)
            }
        })
    }

    const handleFolderFormSubmit = async ({name, isPublic}) => {
        if (folderToEdit) {
            // Rename mode: only update name
            await renameFolder(folderToEdit.id, name)
            showCustomToast({
                message: formatMessage({
                    id: 'wishlist.collection.updated_toast',
                    defaultMessage: 'Your collection has been updated'
                })
            })
        } else if (onNewFolderCreated) {
            // Create-from-move-drawer: don't navigate; callback auto-selects the new folder
            const result = await createFolder(name, isPublic)
            if (result?.id) {
                onNewFolderCreated(result)
            }
            setOnNewFolderCreated(null)
            setPendingCreateName('')
        } else {
            // Normal create: navigate to new folder
            const result = await createFolder(name, isPublic)
            if (result?.id) {
                navigate(buildUrl(`/wishlist/folder/${result.id}`))
            }
        }
    }

    // ---- Derived state ----
    const isViewingFolder = Boolean(folderId)
    const isAtCapacity = (wishListData?.customerProductListItems?.length ?? 0) >= WISHLIST_MAX_ITEMS

    const wishlistItemsLength = Object.keys(wishListItems ?? {}).length
    // CMS empty state is shown only when My Favourites has zero items (nothing saved at all)
    const totalFavouritesItems = wishListData?.customerProductListItems?.length ?? 0
    const hasWishlistItems = totalFavouritesItems > 0
    // isInitialLoading: true only while lists OR first-page products are genuinely fetching.
    // When the active collection is empty (totalWishlistItems === 0) the products query is
    // disabled, so isProductsLoading is irrelevant — we are NOT loading anything.
    const isInitialLoading =
        isListsLoading || (totalWishlistItems > 0 && isProductsLoading && page === 1)
    // If we already have renderable items from cache, keep them on screen instead
    // of swapping to skeletons while a background refresh is in flight.
    const shouldShowInitialSkeleton = isInitialLoading && wishlistItemsLength === 0
    // Empty folder: collection fully loaded and has no items, but the wishlist itself isn't empty
    const isActiveListEmpty = !isListsLoading && totalWishlistItems === 0
    const hasMoreItems = totalWishlistItems > endIndex

    const handleLoadMore = () => {
        setIsLoadingMore(true)
        setPage((prevPage) => prevPage + 1)
    }

    useEffect(() => {
        if (!isProductsLoading && isLoadingMore) {
            setIsLoadingMore(false)
        }
    }, [isProductsLoading, isLoadingMore])

    // ---- Share drawer CMS content ----
    // Pull title + toggle copy/styles from the wishlist-page CMS content. When a
    // value/style is absent we fall back to the hardcoded defaults below.
    const {data: shareCmsData} = useOptimizelyPage(WISHLIST_EMPTY_PAGE_ID)
    const shareCmsContent = shareCmsData?.item || shareCmsData?.items?.[0]
    const resolveCmsText = (prop) => {
        const expanded = (Array.isArray(prop) ? prop[0] : prop)?.ContentLink?.Expanded
        const text = expanded?.TextForWeb
            ? decodeHtmlEntities(expanded.TextForWeb.replace(/<[^>]*>/g, '')).trim()
            : null
        const {styles: resolved} = resolveGDIStyles(
            expanded?.GDITextBlockStylesPerViewType,
            GDI_TEXT_BLOCK_FIELD_MAP
        )
        const sx = Object.keys(resolved).length > 0 ? resolved : null
        return {text, sx}
    }
    const cmsDrawerTitle = resolveCmsText(shareCmsContent?.DrawerTitle)
    const cmsTextAboveToggle = resolveCmsText(shareCmsContent?.TextAboveToggle)
    const cmsToggleText = resolveCmsText(shareCmsContent?.ToggleText)
    const cmsBelowWishlistSection = shareCmsContent?.BelowWishlistSection ?? []
    const cmsBelowCollectionSection = shareCmsContent?.BelowCollectionSection ?? []

    // Empty-collection state: render CMS content when authored, else the code default.
    const emptyCollectionContent = shareCmsContent?.EmptyCollection ?? []
    const hasCmsEmptyCollection = emptyCollectionContent.length > 0

    const seoTitle = isGarage
        ? formatMessage({
              id: 'my.wishlist.page.title.garage',
              defaultMessage: 'View My Wishlist | Garage'
          })
        : formatMessage({
              id: 'my.wishlist.page.title.dynamite',
              defaultMessage: 'View My Wishlist | Dynamite'
          })
    const seoDescription = isGarage
        ? formatMessage({
              id: 'my.wishlist.page.description.garage',
              defaultMessage: 'All of your most coveted pieces lined up in one place. Go ahead and shop your heart out!'
          })
        : formatMessage({
              id: 'my.wishlist.page.description.dynamite',
              defaultMessage: 'All of your most coveted pieces lined up in one place. Go ahead and shop your heart out!'
          })

    return (
        <Box data-testid="wishlist-page">
            <WishlistBreadcrumb />
            <RouteGuard redirectTo={loginPath}>
                <>
                    <Seo title={seoTitle} description={seoDescription} noIndex noFollow />
                    <Box sx={styles.contentContainer}>
                        {/* Page title + mobile-only share icon — hidden on the empty wishlist state */}
                        {!(!isInitialLoading && !hasWishlistItems) && (
                            <Flex sx={styles.pageTitleRow}>
                                <Text textStyle="webH1" sx={styles.pageTitle} ref={headingRef} tabIndex="0">
                                    <FormattedMessage id="global.account.link.wishlist" defaultMessage="Wishlist" />
                                </Text>
                                <Button
                                        variant="unstyled"
                                        onClick={handleShareClick}
                                        sx={{...styles.shareButton, ...styles.pageTitleShareButton}}
                                        aria-label={formatMessage({id: 'wishlist.share_aria', defaultMessage: 'Share wishlist'})}
                                    >
                                        <ShareIcon {...styles.shareIcon} />
                                    </Button>
                            </Flex>
                        )}
                        {!isInitialLoading && !hasWishlistItems ? (
                            <WishlistEmpty />
                        ) : (
                            <>
                                {/* ---- Actions header row ---- */}
                                <Flex sx={styles.actionsHeader}>
                                    {isSelectMode ? (
                                        /* Select mode: show X selected + clear + select all + move + remove */
                                        <>
                                            <Flex sx={styles.actionsLeft}>
                                                <Button
                                                    sx={styles.actionButtonActive}
                                                    onClick={handleDeselectAll}
                                                    iconSpacing="4px"
                                                    rightIcon={<CustomCloseWhite w="20px" h="20px" />}
                                                >
                                                    <FormattedMessage
                                                        id="wishlist.batch.selected_label"
                                                        defaultMessage="{count} selected"
                                                        values={{count: selectedCount}}
                                                    />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    sx={styles.actionButton}
                                                    onClick={allSelected ? () => setSelectedItemIds(new Set()) : handleSelectAll}
                                                >
                                                    {allSelected ? (
                                                        <FormattedMessage id="wishlist.batch.deselect_all" defaultMessage="Deselect All" />
                                                    ) : (
                                                        <FormattedMessage id="wishlist.batch.select_all" defaultMessage="Select All" />
                                                    )}
                                                </Button>
                                                {selectedCount > 0 && (
                                                    <>
                                                        <Button
                                                            variant="outline"
                                                            sx={styles.actionButton}
                                                            onClick={moveItemsDrawerDisclosure.onOpen}
                                                        >
                                                            <FormattedMessage id="wishlist.batch.move_selected" defaultMessage="Move" />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            sx={styles.actionButton}
                                                            onClick={handleOpenRemoveItemsDrawer}
                                                        >
                                                            <FormattedMessage id="wishlist.batch.remove_selected" defaultMessage="Remove" />
                                                        </Button>
                                                    </>
                                                )}
                                            </Flex>
                                        </>
                                    ) : (
                                        /* Normal mode */
                                        <>
                                            <Flex sx={styles.actionsLeft}>
                                                <Button
                                                    variant="outline"
                                                    sx={styles.actionButton}
                                                    onClick={handleCreateFolder}
                                                    data-testid="wishlist-create-folder-button"
                                                    leftIcon={<PlusIcon w="20px" h="20px" />}
                                                >
                                                    <FormattedMessage id="wishlist.folder.create_button" defaultMessage="New Collection" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    sx={styles.actionButton}
                                                    onClick={() => setIsSelectMode(true)}
                                                >
                                                    <FormattedMessage id="wishlist.batch.select_items" defaultMessage="Select Items" />
                                                </Button>
                                                {isViewingFolder && (
                                                    <Button
                                                        variant="outline"
                                                        sx={styles.actionButton}
                                                        onClick={() => {
                                                            const folder = customFolders?.find((f) => f.id === folderId)
                                                            if (folder) handleRenameFolder(folder)
                                                        }}
                                                    >
                                                        <FormattedMessage id="wishlist.folder.edit_collection" defaultMessage="Edit Collection" />
                                                    </Button>
                                                )}
                                            </Flex>
                                <Button
                                                variant="unstyled"
                                                onClick={handleShareClick}
                                                sx={{...styles.shareButton, ...styles.shareButtonDesktop}}
                                                leftIcon={<ShareIcon {...styles.shareIcon} />}
                                                aria-label={formatMessage({id: 'wishlist.share_aria', defaultMessage: 'Share wishlist'})}
                                            >
                                                <Text textStyle="labelSmMd" color="webTypo.textSecondary">
                                                    <FormattedMessage id="wishlist.share_label" defaultMessage="Share" />
                                                </Text>
                                            </Button>
                                        </>
                                    )}
                                </Flex>

                                {/* ---- Desktop: sidebar + content; Mobile: tabs above content ---- */}
                                <Grid sx={styles.pageGrid}>
                                    {/* Folder nav: sidebar on desktop, tab strip on mobile */}
                                    <WishlistFolderNav
                                        wishList={wishListData}
                                        customFolders={(customFolders ?? []).filter(
                                            (f) => f.id !== pendingDeleteFolder?.folder?.id
                                        )}
                                        activeFolderId={folderId ?? wishListData?.id}
                                        style={{minWidth: 0}}
                                    />

                                    {/* Right: product content */}
                                    <Stack spacing={{base: 6, lg: 8}} minW={0}>
                                    {/* Capacity warning */}
                                    {isAtCapacity && (
                                        <Box sx={styles.errorBanner}>
                                            <Text sx={styles.errorBannerText}>
                                                {isGarage ? (
                                                    <FormattedMessage
                                                        defaultMessage="You have reached the maximum number of items for your Wishlist. Please remove some items before adding more."
                                                        id="account_wishlist.wishlist.error.garage"
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        defaultMessage="You have reached the maximum number of items for your Wishlist. Please remove some items before adding more."
                                                        id="account_wishlist.wishlist.error.dynamite"
                                                    />
                                                )}
                                            </Text>
                                        </Box>
                                    )}

                                    {/* Empty collection placeholder */}
                                    {isActiveListEmpty && !isInitialLoading && (
                                        hasCmsEmptyCollection ? (
                                            emptyCollectionContent.map((item, idx) => (
                                                <React.Fragment
                                                    key={`${item?.ContentLink?.Id ?? 'empty-collection'}-${idx}`}
                                                >
                                                    {renderLayoutsSwitch(item?.ContentLink?.Expanded)}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <Stack spacing={0} align="center">
                                                <Box
                                                    sx={styles.emptyFolderBox}
                                                >
                                                    <Text textStyle="webH3" mb={2}>
                                                        <FormattedMessage
                                                            id="wishlist.folder.empty_title"
                                                            defaultMessage="This collection is empty."
                                                        />
                                                    </Text>
                                                    <Text textStyle="bodySmRegular">
                                                        <FormattedMessage
                                                            id="wishlist.folder.empty_subtitle"
                                                            defaultMessage="Start adding or moving from other collections here."
                                                        />
                                                    </Text>
                                                </Box>
                                                <Button
                                                    variant="webPrimary"
                                                    w={{base: '100%', lg: '300px'}}
                                                    onClick={() => navigate(buildUrl('/'))}
                                                >
                                                    <FormattedMessage
                                                        id="wishlist.folder.empty_cta"
                                                        defaultMessage="See New Arrivals"
                                                    />
                                                </Button>
                                            </Stack>
                                        )
                                    )}

                                    {shouldShowInitialSkeleton && (
                                        <SimpleGrid sx={styles.productGrid} w="100%">
                                            {new Array(PRODUCTS_PER_PAGE).fill(0).map((_, index) => (
                                                <ProductTileSkeleton key={index} />
                                            ))}
                                        </SimpleGrid>
                                    )}

                                    {!shouldShowInitialSkeleton && wishListItems && (
                                        <>
                                            <SimpleGrid sx={styles.productGrid} w="100%">
                                                {/* Grid interrupter: shown as first tile when no custom folders exist */}
                                                {!isViewingFolder && !customFolders?.length && (
                                                    <Box
                                                        sx={styles.gridInterrupter}
                                                        data-testid="wishlist-collection-interrupter"
                                                    >
                                                        <WishlistNoCollections />
                                                    </Box>
                                                )}
                                                {Object.keys(wishListItems).map((key) => {
                                                    const item = wishListItems[key]
                                                    const isOOS = item?.product?.inventory?.orderable === false
                                                    const isSelected = selectedItemIds.has(key)

                                                    return (
                                                        <Box key={`stl-tile-${key}`} position="relative">
                                                            {isSelectMode && (
                                                                <Checkbox
                                                                    isChecked={isSelected}
                                                                    onChange={() => handleToggleSelect(key)}
                                                    position="absolute"
                                                                    top="8px"
                                                                    right="8px"
                                                                    zIndex={3}
                                                                    sx={{
                                                                        bg: 'webTheme.backgroundDefault',
                                                                        p: '4px 6px 6px',
                                                                        '& .chakra-checkbox__label': {
                                                                            display: 'none'
                                                                        }
                                                                    }}
                                                                    aria-label={formatMessage(
                                                                        {id: 'wishlist.select_item_aria', defaultMessage: 'Select {name}'},
                                                                        {name: item?.product?.name ?? key}
                                                                    )}
                                                                />
                                                            )}
                                                            <ProductTile
                                                                variant="wishlist"
                                                                data-testid="wishlist-product-tile"
                                                                product={item?.product}
                                                                enableFavourite={!isSelectMode}
                                                                isFavourite={true}
                                                                showSwatches={true}
                                                                showVideo={true}
                                                                dynamicImageProps={{widths: ['50vw', '50vw', '33vw', '33vw']}}
                                                                isOOS={isOOS}
                                                                isWishlistPage={true}
                                                                onFavouriteToggle={() => handleRequestRemoveItem(item)}
                                                                sx={isOOS ? styles.oosProductTile : undefined}
                                                                onClick={isSelectMode ? () => handleToggleSelect(key) : undefined}
                                                            />
                                                        </Box>
                                                    )
                                                })}
                                            </SimpleGrid>

                                            {isLoadingMore && (
                                                <SimpleGrid sx={styles.productGridLoadMore}>
                                                    {new Array(Math.max(0, Math.min(PRODUCTS_PER_PAGE, totalWishlistItems - endIndex)))
                                                        .fill(0)
                                                        .map((_, index) => (
                                                            <ProductTileSkeleton key={`loading-more-${index}`} />
                                                        ))}
                                                </SimpleGrid>
                                            )}

                                            {hasMoreItems && !isLoadingMore && (
                                                <Flex sx={styles.loadMoreContainer}>
                                                    <Button variant="solid" onClick={handleLoadMore} sx={styles.loadMoreButton}>
                                                        <FormattedMessage defaultMessage="Show More" id="plp.button.show.more" />
                                                    </Button>
                                                </Flex>
                                            )}
                                        </>
                                    )}

                                    <UnavailableProductConfirmationModal
                                        productItems={activeList?.customerProductListItems}
                                        handleUnavailableProducts={handleUnavailableProducts}
                                    />
                                </Stack>
                            </Grid>
                            </>
                        )}

                        <GDIDynamicContentProvider value={{forceCarouselBleed: true}}>
                            {activeList?.type === "wish_list" && hasWishlistItems &&
                                (
                                    <Box data-testid="below-wishlist-section">
                                        {cmsBelowWishlistSection.map((item, idx) => (
                                            <React.Fragment key={`${item?.ContentLink?.Id ?? 'below-wishlist-section'}-${idx}`}>
                                                {renderLayoutsSwitch(item?.ContentLink?.Expanded)}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                )
                            }

                            {activeList?.type === "custom_1" &&
                                (
                                    <Box data-testid="below-collection-section">
                                        {cmsBelowCollectionSection.map((item, idx) => (
                                            <React.Fragment key={`${item?.ContentLink?.Id ?? 'below-collection-section'}-${idx}`}>
                                                {renderLayoutsSwitch(item?.ContentLink?.Expanded)}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                )
                            }
                        </GDIDynamicContentProvider>

                    </Box>

                    {/* Share Collection drawer */}
                    <WebDrawer
                        isOpen={shareDisclosure.isOpen}
                        onClose={handleShareDrawerCancel}
                        title={cmsDrawerTitle.text || 'Share Collection'}
                        titleSx={cmsDrawerTitle.sx || undefined}
                        size="full"
                    >
                        <Stack spacing={6}>
                            <Stack spacing={3}>
                                <Text textStyle="bodySmBold" sx={cmsTextAboveToggle.sx || undefined}>
                                    {cmsTextAboveToggle.text || (
                                        <FormattedMessage
                                            id="wishlist.share.visibility_label"
                                            defaultMessage="Collection visibility"
                                        />
                                    )}
                                </Text>
                                <Flex align="center" gap={3}>
                                    <Switch
                                        isChecked={isSharePublic}
                                        onChange={(e) => setIsSharePublic(e.target.checked)}
                                        size="md"
                                        sx={styles.shareSwitch}
                                    />
                                    <Text textStyle="bodySmRegular" sx={cmsToggleText.sx || undefined}>
                                        {cmsToggleText.text ? (
                                            cmsToggleText.text
                                        ) : isSharePublic ? (
                                            <FormattedMessage
                                                id="wishlist.share.visibility_on"
                                                defaultMessage="Your folder can be seen and shared with others."
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="wishlist.share.visibility_off"
                                                defaultMessage="Your folder is private, toggle ON to be able to share with others."
                                            />
                                        )}
                                    </Text>
                                </Flex>
                            </Stack>

                            {isSharePublic && (
                                <Stack spacing={4}>
                                    <Flex
                                        as="button"
                                        type="button"
                                        align="center"
                                        gap={3}
                                        cursor="pointer"
                                        onClick={handleCopyLink}
                                        _hover={{opacity: 0.7}}
                                        textAlign="left"
                                        bg="transparent"
                                        border="none"
                                        p={0}
                                    >
                                        <LinkIcon w="20px" h="20px" flexShrink={0} />
                                        <Text textStyle="bodySmLink">
                                            <FormattedMessage
                                                id="wishlist.share.copy_link"
                                                defaultMessage="Copy link to clipboard"
                                            />
                                        </Text>
                                    </Flex>
                                    <Flex
                                        as="button"
                                        type="button"
                                        align="center"
                                        gap={3}
                                        cursor="pointer"
                                        onClick={handleShareAction}
                                        _hover={{opacity: 0.7}}
                                        textAlign="left"
                                        bg="transparent"
                                        border="none"
                                        p={0}
                                    >
                                        <ShareIcon w="20px" h="20px" flexShrink={0} />
                                        <Text textStyle="bodySmLink">
                                            <FormattedMessage
                                                id="wishlist.share.share_btn"
                                                defaultMessage="Share"
                                            />
                                        </Text>
                                    </Flex>
                                </Stack>
                            )}

                            <Stack spacing={3} pt={2}>
                                <Button type="button" variant="webPrimary" w="100%" onClick={handleShareDrawerSave}>
                                    <FormattedMessage id="wishlist.share.save_btn" defaultMessage="Save" />
                                </Button>
                                <Button type="button" variant="webSecondaryOutline" w="100%" onClick={handleShareDrawerCancel}>
                                    <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                                </Button>
                            </Stack>
                        </Stack>
                    </WebDrawer>

                    {/* Folder create/rename/delete modal */}
                    <WishlistFolderFormModal
                        isOpen={folderFormDisclosure.isOpen}
                        onClose={() => {
                            folderFormDisclosure.onClose()
                            setPendingCreateName('')
                            setOnNewFolderCreated(null)
                        }}
                        onSubmit={handleFolderFormSubmit}
                        onDelete={handleDeleteConfirm}
                        folder={folderToEdit}
                        folderItemCount={folderToEdit ? (activeList?.customerProductListItems?.length ?? 0) : 0}
                        initialName={pendingCreateName}
                    />

                    {/* Remove items drawer — used for both batch and per-item remove via heart icon */}
                    <WishlistRemoveItemsDrawer
                        isOpen={removeItemsDrawerDisclosure.isOpen}
                        onClose={() => {
                            removeItemsDrawerDisclosure.onClose()
                            // If opened via heart icon (not batch select mode), clear the programmatic selection
                            if (!isSelectMode) setSelectedItemIds(new Set())
                        }}
                        collectionName={activeList?.name}
                        isViewingFolder={isViewingFolder}
                        onConfirm={handleRemoveItemsConfirm}
                        selectedCount={selectedItemIds.size}
                    />

                    {/* Move items drawer */}
                    <WishlistMoveItemsDrawer
                        isOpen={moveItemsDrawerDisclosure.isOpen}
                        onClose={moveItemsDrawerDisclosure.onClose}
                        selectedCount={selectedItemIds.size}
                        collections={customFolders.filter((f) => f.id !== activeList?.id)}
                        isFromFavourites={!isViewingFolder}
                        onApply={handleBatchMoveApply}
                        onOpenCreateModal={(name, onCreated) => {
                            setFolderToEdit(null)
                            setPendingCreateName(name || '')
                            setOnNewFolderCreated(() => onCreated)
                            folderFormDisclosure.onOpen()
                        }}
                    />
                </>
            </RouteGuard>
        </Box>
    )
}

WishlistPage.getTemplateName = () => 'wishlist'

export default WishlistPage
